import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Calendar, TrendingUp } from 'lucide-react';

const CompactLotteryCard = ({ lottery, latestDraw, isPending = false }) => {
  const { currentLang, t } = useLanguage();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Pending';
    // Preserve the calendar day regardless of the viewer's timezone.
  const date = new Date(String(dateStr).slice(0, 10) + 'T00:00:00Z');
  if (Number.isNaN(date.getTime())) return 'Pending';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', timeZone: 'UTC' 
    });
  };

  const formatJackpot = (amount, currency) => {
    if (!amount) return 'Pending';
    if (amount < 1000000) {
      return `${(amount / 1000).toFixed(0)}K ${currency || 'EUR'}`;
    }
    return `${(amount / 1000000).toFixed(1)}M ${currency || 'EUR'}`;
  };

  const getCountryFlag = (country) => {
    const flags = {
      Switzerland: '🇨🇭',
      Italy: '🇮🇹',
      Spain: '🇪🇸',
      Europe: '🇪🇺',
      Germany: '🇩🇪',
      UK: '🇬🇧',
      Ireland: '🇮🇪',
      USA: '🇺🇸',
      Australia: '🇦🇺',
      Canada: '🇨🇦',
      Brazil: '🇧🇷',
    };
    return flags[country] || '🌍';
  };

  // For pending cards: render as div (not Link) to prevent any navigation
  if (isPending) {
    return (
      <div
        className="flex flex-col h-full bg-gray-900/40 border border-gray-700/50 rounded-lg p-2.5 opacity-75 cursor-not-allowed relative pointer-events-none"
        aria-disabled="true"
        tabIndex={-1}
      >
        {/* Pending Badge */}
        <div className="absolute top-2 right-2 bg-gray-700/80 text-gray-400 text-xs px-2 py-0.5 rounded-full">
          PENDING
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-bold text-white truncate">
              {lottery.name}
            </h3>
          </div>
          <span className="text-base ml-1.5 flex-shrink-0">
            {getCountryFlag(lottery.country)}
          </span>
        </div>

        {/* Coming Soon placeholder */}
        <div className="flex-grow mb-1.5 flex items-center justify-center">
          <span className="text-gray-500 text-xs">{t('lottery.comingSoon')}</span>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-1.5 border-t border-gray-700/30">
          <div className="text-gray-500 text-[9px]">{t('lottery.comingSoon')}</div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/${currentLang}/lottery/${lottery.code}`}
      className="flex flex-col h-full bg-gray-900/40 border border-gray-700/50 rounded-lg p-2.5 hover:border-amber-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/5 group relative"
    >
      {/* Pending Badge */}
      {isPending && (
        <div className="absolute top-2 right-2 bg-gray-700/80 text-gray-400 text-xs px-2 py-0.5 rounded-full">
          {t('lottery.pending')}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors truncate">
            {lottery.name}
          </h3>
        </div>
        <span className="text-base ml-1.5 flex-shrink-0">
          {getCountryFlag(lottery.country)}
        </span>
      </div>

      {/* Numbers */}
      <div className="flex-grow mb-1.5">
        {!isPending && latestDraw ? (
          <>
            <div className="flex flex-wrap gap-0.5 mb-1">
              {/* MAIN NUMBERS */}
              {latestDraw.main_numbers?.slice(0, 6).map((num, idx) => (
                <span
                  key={idx}
                  className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500/30 to-amber-600/20 text-amber-400 text-[10px] flex items-center justify-center font-semibold"
                >
                  {num}
                </span>
              ))}

              {/* SPECIAL CASE: SUPERENALOTTO */}
              {lottery.code === 'superenalotto' && (
                  <>
                    {/* JOLLY (from jolly or jolly_number) */}
                    {(latestDraw.jolly ?? latestDraw.jolly_number) && (
                      <div className="flex items-center gap-0.5">
                        <span className="text-[9px] text-gray-400">J:</span>
                        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-400 text-[10px] flex items-center justify-center font-semibold">
                          {latestDraw.jolly ?? latestDraw.jolly_number}
                        </span>
                      </div>
                    )}

                    {/* SUPERSTAR (from superstar field) */}
                    {latestDraw.superstar && (
                      <div className="flex items-center gap-0.5">
                        <span className="text-[9px] text-gray-400">S:</span>
                        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-green-500/30 to-green-600/20 text-green-400 text-[10px] flex items-center justify-center font-semibold">
                          {latestDraw.superstar}
                        </span>
                      </div>
                    )}
                  </>
                )}

              {/* SPECIAL CASE: LA PRIMITIVA */}
              {lottery.code === 'laprimitiva' && (
                  <>
                    {/* COMPLEMENTARIO - from explicit field */}
                    {latestDraw.complementario != null && (
                      <div className="flex items-center gap-0.5">
                        <span className="text-[9px] text-gray-400">C:</span>
                        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-400 text-[10px] flex items-center justify-center font-semibold">
                          {latestDraw.complementario}
                        </span>
                      </div>
                    )}

                    {/* REINTEGRO - from explicit field */}
                    {latestDraw.reintegro != null && (
                      <div className="flex items-center gap-0.5">
                        <span className="text-[9px] text-gray-400">R:</span>
                        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-400 text-[10px] flex items-center justify-center font-semibold">
                          {latestDraw.reintegro}
                        </span>
                      </div>
                    )}
                  </>
                )}

              {/* SPECIAL CASE: GERMAN LOTTO - Superzahl */}
              {lottery.code === 'german-lotto' && latestDraw.superzahl != null && (
                <div className="flex items-center gap-0.5">
                  <span className="text-[9px] text-gray-400">SZ:</span>
                  <span className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500/30 to-purple-600/20 text-purple-400 text-[10px] flex items-center justify-center font-semibold">
                    {latestDraw.superzahl}
                  </span>
                </div>
              )}

              {/* DEFAULT BONUS HANDLING (OTHER LOTTERIES) */}
              {lottery.code !== 'superenalotto' &&
                lottery.code !== 'laprimitiva' &&
                lottery.code !== 'german-lotto' &&
                latestDraw.bonus_numbers?.slice(0, 2).map((num, idx) => (
                  <span
                    key={`bonus-${idx}`}
                    className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-400 text-[10px] flex items-center justify-center font-semibold"
                  >
                    {num}
                  </span>
                ))}
            </div>

            <div className="flex items-center gap-1 text-gray-500 text-[10px] mt-0.5">
              <Calendar className="w-2.5 h-2.5" />
              <span className="text-gray-400">Draw:</span>
              <span>{formatDate(latestDraw.date || latestDraw.draw_date)}</span>
            </div>
          </>
        ) : (
          <div className="text-gray-500 text-[10px] italic">No recent draws</div>
        )}
      </div>

      {/* Next Draw Date */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-800/50">
        <div className="flex items-center gap-0.5">
          <Calendar className="w-2.5 h-2.5 text-amber-400" />
          <span className="text-gray-400 text-[10px]">Next:</span>
        </div>
        <span className="text-amber-400 text-[10px] font-medium">
          {lottery.next_draw_date ? formatDate(lottery.next_draw_date) : 'Pending'}
        </span>
      </div>

      {/* Jackpot - Shows NEXT draw estimate */}
      <div className="flex items-center justify-between pt-1 mt-auto">
        <div className="flex items-center gap-0.5">
          <TrendingUp className="w-2.5 h-2.5 text-green-400" />
          <span className="text-gray-400 text-[10px]">{lottery.code === 'eurodreams' ? 'Monthly prize:' : 'Next Est:'}</span>
        </div>
        <span
          className={`font-bold text-[10px] ${
            !isPending && (lottery.next_jackpot_estimate || lottery.current_jackpot)
              ? 'text-green-400'
              : 'text-gray-500'
          }`}
        >
          {formatJackpot(lottery.next_jackpot_estimate || lottery.current_jackpot, lottery.jackpot_currency)}{lottery.code === 'eurodreams' && (lottery.next_jackpot_estimate || lottery.current_jackpot) ? ' / month · 30 years' : ''}
        </span>
      </div>
    </Link>
  );
};

export default CompactLotteryCard;
